import type { ReactNode, ReactElement } from 'react';
import { Icon, type IconName } from './Icons';
import arrowLight from './assets/arrow-light.svg';
import arrow from './assets/arrow.svg';

export function PageHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}): ReactElement {
  return (
    <div className="-mt-2">
      <h1 className="lh-h1 m-0">{title}</h1>
      {subtitle ? (
        <p className="m-0 mt-2 max-w-[640px] text-base leading-[1.6]">{subtitle}</p>
      ) : null}
    </div>
  );
}

export function Section({
  title,
  subtitle,
  aside,
  children,
}: {
  title: string;
  subtitle?: string;
  aside?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="lh-h2 m-0">{title}</h2>
          {subtitle ? <p className="lh-sub m-0 mt-1.5 max-w-[620px]">{subtitle}</p> : null}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Panel({
  children,
  tone = 'default',
  className = '',
}: {
  children: ReactNode;
  tone?: 'default' | 'sand';
  className?: string;
}): ReactElement {
  return (
    <div className={`lh-panel ${tone === 'sand' ? 'lh-panel--sand' : ''} ${className}`}>
      {children}
    </div>
  );
}

export function PanelHead({
  title,
  subtitle,
  aside,
}: {
  title: string;
  subtitle?: string;
  aside?: ReactNode;
}): ReactElement {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h3 className="lh-h3 m-0">{title}</h3>
        {subtitle ? <p className="lh-sub m-0 mt-0.5">{subtitle}</p> : null}
      </div>
      {aside}
    </div>
  );
}

export function Muted({ children }: { children: ReactNode }): ReactElement {
  return <span className="lh-sub">{children}</span>;
}

export function Chip({
  children,
  background,
  color,
  dot,
}: {
  children: ReactNode;
  background?: string;
  color?: string;
  dot?: boolean;
}): ReactElement {
  return (
    <span className="lh-chip" style={{ background, color }}>
      {dot ? <span className="block size-1.5 rounded-full bg-current" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/** A thin determinate bar; `width` is a percentage and is clamped to 0–100. */
export function Bar({ width, label }: { width: number; label?: string }): ReactElement {
  return (
    <div className="lh-track" role="img" aria-label={label}>
      <span style={{ width: `${Math.min(Math.max(width, 0), 100)}%` }} />
    </div>
  );
}

/**
 * Shown wherever a figure is withheld because too few people stand behind it.
 *
 * Deliberately explicit: an empty panel reads as a bug, and a Lead HR who does not
 * understand why a row is missing will ask for it to be removed.
 */
export function Suppressed({ message }: { message: string }): ReactElement {
  return (
    <div
      className="flex items-center gap-2 rounded-lg px-3 py-2"
      style={{ background: 'var(--lh-lblue)' }}
    >
      <span aria-hidden="true">🔒</span>
      <span className="lh-small" style={{ fontSize: 13 }}>
        {message}
      </span>
    </div>
  );
}

export function Notice({
  children,
  tone = 'error',
}: {
  children: ReactNode;
  tone?: 'error' | 'success';
}): ReactElement {
  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      className="m-0 rounded-lg px-3 py-2 text-[13px] leading-[1.45]"
      style={{ background: tone === 'error' ? 'var(--lh-red)' : 'var(--lh-lgreen)' }}
    >
      {children}
    </p>
  );
}

export function Button({
  children,
  onClick,
  disabled,
  type = 'button',
  variant = 'secondary',
  size = 'large',
  icon = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  variant?: 'primary' | 'secondary';
  size?: 'large' | 'small';
  /** Trailing arrow, used on the primary call to action. */
  icon?: boolean;
}): ReactElement {
  const classes = [
    'lh-btn',
    variant === 'primary' ? 'lh-btn--primary' : '',
    size === 'small' ? 'lh-btn--small' : '',
  ];
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={classes.join(' ')}>
      {children}
      {icon ? <img src={arrowLight} alt="" width={21} height={21} /> : null}
    </button>
  );
}

/** Underlined inline action with a trailing arrow, as used in the attention list. */
export function LinkAction({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}): ReactElement {
  return (
    <button type="button" onClick={onClick} className="lh-link">
      {children}
      <img src={arrow} alt="" width={16} height={16} />
    </button>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
  label: string;
}): ReactElement {
  return (
    <div className="lh-seg" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function FilterBar({ children }: { children: ReactNode }): ReactElement {
  return (
    <div
      className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg px-5 py-4"
      style={{ border: '1px solid var(--lh-line)', background: 'var(--lh-card)' }}
    >
      {children}
    </div>
  );
}

export function FilterSelect({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="lh-small" style={{ color: 'var(--lh-muted)' }}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="lh-select"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function IconTile({
  icon,
  background,
}: {
  icon: IconName;
  background: string;
}): ReactElement {
  return (
    <span
      className="flex size-9 flex-none items-center justify-center rounded-lg"
      style={{ background }}
    >
      <Icon name={icon} size={20} />
    </span>
  );
}

export function Avatar({
  initials,
  size = 36,
  tone = 'light',
}: {
  initials: string;
  size?: number;
  tone?: 'light' | 'dark';
}): ReactElement {
  const dark = tone === 'dark';
  return (
    <div
      className="flex flex-none items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        background: dark ? 'var(--lh-ink)' : 'var(--lh-lgreen)',
        color: dark ? 'var(--lh-offwhite)' : 'var(--lh-ink)',
        font: dark ? `400 ${size * 0.34}px/1 var(--lh-font-book)` : '600 12px/1 var(--lh-font-ui)',
      }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}
